import React from 'react';
import { Browser } from '@capacitor/browser';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  Heading,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { Device, DeviceOperatingState } from 'clipsal-cortex-types/src/api';
import { SensiboDevice } from 'clipsal-cortex-types/src/api/api-sensibo-devices';
import { DialogProps } from 'clipsal-cortex-types/src/common/chakra-extension-types';
import { CATCH_POWER_MANUFACTURER_ID } from 'clipsal-cortex-utils/src/constants/common-constants';

import sensiboIconImg from '../../../assets/images/sensibo_icon.png';
import { useViewportType } from '../../../common/hooks/use-viewport-type';
import { useGetSensiboDevicesQuery } from '../../live/sensibo-integration/sensiboApi';
import { DEVICE_TYPE_TO_DISPLAY_DATA, getOperatingStateUIConfig } from '../../profile/system-details/constants';
import { checkIfOperatingStateErrorCanBeIgnored } from '../../profile/system-details/system-details-helpers';
import { SupportModal } from '../../support/SupportModal';
import { selectMeterByManufacturerId, selectSite } from '../siteSlice';
import {
  EV_DEVICE_TROUBLESHOOTING_ARTICLE_URL,
  HYBRID_INVERTER_TROUBLESHOOTING_ARTICLE_URL,
  METER_CATCH_TROUBLESHOOTING_ARTICLE_URL,
  METER_WATTWATCHERS_TROUBLESHOOTING_ARTICLE_URL,
  SENSIBO_DEVICE_TROUBLESHOOTING_ARTICLE_URL,
} from './constants';

type DeviceAlertInfoDialogProps = DialogProps;

export function DeviceAlertInfoDialog({ isOpen, onClose }: DeviceAlertInfoDialogProps) {
  const { isOpen: isSupportDialogOpen, onOpen: onOpenSupportDialog, onClose: onCloseSupportDialog } = useDisclosure();
  const commonProps = { isOpen, onClose, onOpenSupportDialog };
  const { isDesktopViewport } = useViewportType();

  return (
    <>
      {isDesktopViewport ? <DeviceAlertInfoModal {...commonProps} /> : <DeviceAlertInfoBottomDrawer {...commonProps} />}
      {isSupportDialogOpen && <SupportModal isOpen={isSupportDialogOpen} onClose={onCloseSupportDialog} />}
    </>
  );
}

type CommonDeviceAlertInfoDialogProps = {
  onOpenSupportDialog: () => void;
} & DialogProps;

function DeviceAlertInfoModal({ isOpen, onClose, onOpenSupportDialog }: CommonDeviceAlertInfoDialogProps) {
  return (
    <Modal scrollBehavior={'inside'} size={'xl'} isCentered={true} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent data-testid="device-alert-info-modal">
        <ModalHeader as={Center}>Device Alert Information</ModalHeader>
        <ModalCloseButton data-testid="device-alert-info-modal-close-btn" />
        <ModalBody>
          <DeviceAlertList onOpenSupportDialog={onOpenSupportDialog} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function DeviceAlertInfoBottomDrawer({ isOpen, onClose, onOpenSupportDialog }: CommonDeviceAlertInfoDialogProps) {
  const drawerHeaderBorderColor = useColorModeValue('dusk005.500', 'borderGrey.500');

  return (
    <Drawer onClose={onClose} placement={'bottom'} isOpen={isOpen}>
      <DrawerOverlay />
      <DrawerContent data-testid="device-alert-info-drawer">
        <DrawerHeader borderColor={drawerHeaderBorderColor}>
          <Center position="relative">
            <Button
              data-testid="device-alert-info-drawer-close-btn"
              variant="ghost"
              onClick={onClose}
              left={0}
              position="absolute"
              size="sm"
            >
              <CloseIcon w={[3, 4]} h={[3, 4]} />
            </Button>
            <Text>Device Alert Information</Text>
          </Center>
        </DrawerHeader>
        <DrawerBody p={4}>
          <DeviceAlertList onOpenSupportDialog={onOpenSupportDialog} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function DeviceAlertList({ onOpenSupportDialog }: { onOpenSupportDialog: () => void }) {
  const { devices, integrations, site_id: siteID } = useSelector(selectSite);
  const { data } = useGetSensiboDevicesQuery(siteID, {
    skip: !integrations.sensibo,
  });

  return (
    <Box>
      <Alert mb={3} status="info">
        <AlertIcon />
        <Box>
          <Text>
            When there is an issue with one of your devices, the data you see in the Cortex app will be inaccurate.
          </Text>
          <Text mt={1}>
            <b>NOTE:</b> Data can take a few hours to be reflected in the app after troubleshooting.
          </Text>
        </Box>
      </Alert>

      <Heading size="md">Device Alerts</Heading>
      {devices!
        .filter((device) => device.operating_state && ![null, 'NORMAL'].includes(device.operating_state))
        .map((device, index) => (
          <DeviceAlertListItem
            key={device.model}
            index={index}
            device={device}
            onOpenSupportDialog={onOpenSupportDialog}
          />
        ))}
      {data
        ?.filter(
          (device: SensiboDevice) => device.operating_state && ![null, 'NORMAL'].includes(device.operating_state)
        )
        .map((device, index) => (
          <SensiboDeviceAlertListItem
            key={device.id}
            index={index}
            device={device}
            onOpenSupportDialog={onOpenSupportDialog}
          />
        ))}
    </Box>
  );
}

function DeviceAlertListItem({
  index,
  device,
  onOpenSupportDialog,
}: {
  index: number;
  device: Device;
  onOpenSupportDialog: () => void;
}) {
  const {
    device_type: deviceType,
    model,
    manufacturer_name: manufacturerName,
    operating_state: operatingState,
  } = device;
  const iconProperty = useColorModeValue('iconLight', 'iconDark');
  const canIgnoreError = checkIfOperatingStateErrorCanBeIgnored(device);
  const operatingStateConfig = operatingState && getOperatingStateUIConfig(operatingState);
  const isCatchMeter = !!useSelector(selectMeterByManufacturerId(CATCH_POWER_MANUFACTURER_ID));

  const handleButtonClick = async () => {
    /* istanbul ignore else -- @preserve */
    if (device.device_type === 'METER') {
      /* istanbul ignore else -- @preserve */
      if (device.operating_state === 'SUBSCRIPTION_EXPIRED') {
        onOpenSupportDialog();
      } else if (isCatchMeter) {
        await Browser.open({
          url: METER_CATCH_TROUBLESHOOTING_ARTICLE_URL,
        });
      } else {
        await Browser.open({
          url: METER_WATTWATCHERS_TROUBLESHOOTING_ARTICLE_URL,
        });
      }
    } else if (device.device_type === 'EV_CHARGER') {
      await Browser.open({
        url: EV_DEVICE_TROUBLESHOOTING_ARTICLE_URL,
      });
    } else {
      await Browser.open({
        url: HYBRID_INVERTER_TROUBLESHOOTING_ARTICLE_URL,
      });
    }
  };

  return (
    <Grid w={'100%'} gridColumnGap={2} my={2} pt={2} key={`device-${index}`} templateColumns={'50px 7fr 3fr'}>
      <Center>
        <Image src={DEVICE_TYPE_TO_DISPLAY_DATA[deviceType][iconProperty]} alt={'Device icon'} />
      </Center>

      <Flex justify={'center'} direction={'column'}>
        <Heading size={'sm'}>{DEVICE_TYPE_TO_DISPLAY_DATA[deviceType].title}</Heading>
        <Text fontSize={'sm'} color={'textGrey.500'}>
          {manufacturerName} {model}
        </Text>
        {!canIgnoreError && operatingStateConfig && (
          <Text fontSize={'sm'} color={operatingStateConfig.color}>
            {operatingStateConfig.text}
          </Text>
        )}
      </Flex>

      <Center>
        <Button rounded={20} onClick={() => handleButtonClick()} colorScheme="dusk100">
          {device.operating_state !== 'SUBSCRIPTION_EXPIRED' ? 'How to resolve' : 'Contact support'}
        </Button>
      </Center>
    </Grid>
  );
}

function SensiboDeviceAlertListItem({
  index,
  device,
  onOpenSupportDialog,
}: {
  index: number;
  device: SensiboDevice;
  onOpenSupportDialog: () => void;
}) {
  const { name, operating_state: operatingState } = device;
  const operatingStateConfig = operatingState && getOperatingStateUIConfig(operatingState as DeviceOperatingState);

  const handleButtonClick = async () => {
    /* istanbul ignore else -- @preserve */
    if (device.operating_state === 'SUBSCRIPTION_EXPIRED') {
      onOpenSupportDialog();
    } else {
      await Browser.open({
        url: SENSIBO_DEVICE_TROUBLESHOOTING_ARTICLE_URL,
      });
    }
  };

  return (
    <Grid
      w={'100%'}
      gridColumnGap={2}
      my={2}
      pt={2}
      key={`device-${index}`}
      templateColumns={'50px 7fr 3fr'}
      data-testid="sensibo-device-alert-list-item"
    >
      <Center border={'1px solid black'} borderRadius={5} padding={3} margin="2px">
        <Image src={sensiboIconImg} alt={'Device icon'} />
      </Center>

      <Flex justify={'center'} direction={'column'}>
        <Heading size={'sm'}>{name}</Heading>
        {operatingStateConfig && (
          <Text fontSize={'sm'} color={operatingStateConfig.color}>
            {operatingStateConfig.text}
          </Text>
        )}
      </Flex>

      <Center>
        <Button rounded={20} onClick={() => handleButtonClick()} colorScheme="dusk100">
          {device.operating_state !== 'SUBSCRIPTION_EXPIRED' ? 'How to resolve' : 'Contact support'}
        </Button>
      </Center>
    </Grid>
  );
}
